import { type ComputedRef, type Ref, computed } from 'vue';

import { HALLOWEEN_ICONS, NAV_LABEL_ICONS } from '~/components/seasonal/halloween-icons';
import { THANKSGIVING_ICONS, THANKSGIVING_NAV_ICONS } from '~/components/seasonal/thanksgiving-icons';
import { useSeasonalThemeStore } from '~/store/seasonal-theme';
import { useThanksgivingThemeStore } from '~/store/thanksgiving-theme';

type NavLike = { icon?: unknown; items?: unknown[]; label?: string } | null | undefined | false;

// The live season's icon for a nav entry (Halloween first, then Thanksgiving), or the icon it already has when no
// season's icon egg is on or the label is unmapped.
export function seasonalNavIcon(label: string | undefined, icon: unknown): unknown {
  if (!label) return icon;

  const halloween = NAV_LABEL_ICONS[label];
  if (halloween && useSeasonalThemeStore().isEggEnabled('icons')) return HALLOWEEN_ICONS[halloween];

  const thanksgiving = THANKSGIVING_NAV_ICONS[label];
  if (thanksgiving && useThanksgivingThemeStore().isEggEnabled('icons')) return THANKSGIVING_ICONS[thanksgiving];

  return icon;
}

function decorate<T extends NavLike>(item: T): T {
  if (!item || typeof item !== 'object') return item;

  const items = Array.isArray(item.items) ? item.items.map(child => decorate(child as NavLike)) : item.items;
  const icon = 'icon' in item ? seasonalNavIcon(item.label, item.icon) : undefined;

  return { ...item, ...(items === undefined ? {} : { items }), ...(icon === undefined ? {} : { icon }) };
}

// Wraps a nav tree so every entry (at any depth) shows the live season's icon.
export function useSeasonalNav<T extends NavLike>(links: Ref<T[]>): ComputedRef<T[]> {
  return computed(() => links.value.map(decorate));
}
